/**
 * CR500 Phase 6A: V2 Rollout Configuration
 * CR310: DEPRECATED - V1 has been removed, V2 is now the only renderer.
 * 
 * This file is kept for backwards compatibility with existing code
 * that may reference these types and functions.
 * 
 * @deprecated V2 is now the default renderer. Use dealCardSpecV2_enabled for emergency toggle only.
 */

import { supabase } from "@/integrations/supabase/client";

// ================================================================
// TYPES (Kept for backwards compatibility)
// ================================================================

/** @deprecated Rollout modes are no longer used */
export type RolloutMode = "off" | "allowlist" | "percent" | "on";

/** @deprecated Rollout config is no longer used */
export interface DealCardV2RolloutConfig {
  enabled: boolean;
  mode: RolloutMode;
  allowlist: string[];
  percent: number;
  updatedAt?: string;
}

/** @deprecated V2 is always enabled */
export const DEFAULT_ROLLOUT_CONFIG: DealCardV2RolloutConfig = {
  enabled: true,
  mode: "on",
  allowlist: [],
  percent: 100,
};

const ROLLOUT_CONFIG_KEY = "deal_card_v2_rollout";
const SESSION_ID_KEY = "deal_card_v2_session";

// ================================================================
// SESSION MANAGEMENT (for stable canary assignment)
// ================================================================

/**
 * Get or create a stable session ID for canary rollout.
 * This ensures the same user gets consistent V2 assignment across refreshes.
 */
export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Get the current session's bucket (0-99) for percent rollout.
 * Useful for admin display.
 */
export function getSessionBucket(): number {
  return hashToPercent(getSessionId());
}

/**
 * Simple hash function for percent-based rollout.
 * Returns a number 0-99 based on the session ID.
 */
function hashToPercent(sessionId: string): number {
  let hash = 0;
  for (let i = 0; i < sessionId.length; i++) {
    const char = sessionId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 100;
}

// ================================================================
// CONFIG LOADING
// ================================================================

let cachedConfig: DealCardV2RolloutConfig | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 60000; // 1 minute

/**
 * Fetch the rollout config from the database.
 */
export async function fetchRolloutConfig(): Promise<DealCardV2RolloutConfig> {
  const now = Date.now();
  
  // Return cached if fresh
  if (cachedConfig && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedConfig;
  }
  
  try {
    const { data, error } = await supabase
      .from("app_config")
      .select("value_json, updated_at")
      .eq("key", ROLLOUT_CONFIG_KEY)
      .maybeSingle();
    
    if (error) {
      console.error("[V2Rollout] Failed to fetch config:", error);
      return cachedConfig ?? DEFAULT_ROLLOUT_CONFIG;
    }
    
    if (!data) {
      cachedConfig = DEFAULT_ROLLOUT_CONFIG;
      cacheTimestamp = now;
      return cachedConfig;
    }
    
    const config = data.value_json as unknown as DealCardV2RolloutConfig;
    cachedConfig = {
      enabled: config.enabled ?? false,
      mode: config.mode ?? "off",
      allowlist: config.allowlist ?? [],
      percent: config.percent ?? 0,
      updatedAt: data.updated_at,
    };
    cacheTimestamp = now;
    
    return cachedConfig;
  } catch (err) {
    console.error("[V2Rollout] Error fetching config:", err);
    return cachedConfig ?? DEFAULT_ROLLOUT_CONFIG;
  }
}

/**
 * Clear the config cache (for testing/admin updates).
 */
export function clearRolloutConfigCache(): void {
  cachedConfig = null;
  cacheTimestamp = 0;
}

// ================================================================
// ROLLOUT DECISION LOGIC (Deprecated)
// ================================================================

/** @deprecated Rollout decisions are no longer needed - V2 is always used */
export interface RolloutDecision {
  useV2: boolean;
  reason: "query_override" | "rollout_off" | "rollout_on" | "allowlist_match" | "allowlist_miss" | "percent_in" | "percent_out" | "admin_mode" | "v2_default";
  config: DealCardV2RolloutConfig;
  sessionHash?: number;
}

/**
 * @deprecated V2 is now always used. This function returns V2 by default.
 */
export function computeRolloutDecision(
  config: DealCardV2RolloutConfig = DEFAULT_ROLLOUT_CONFIG,
  options: {
    queryOverride?: boolean | null;
    userId?: string;
    isAdmin?: boolean;
  } = {}
): RolloutDecision {
  // CR310: Always return V2 - V1 has been removed
  return { useV2: true, reason: "v2_default", config };
}

// ================================================================
// ADMIN: UPDATE ROLLOUT CONFIG (Deprecated but functional)
// ================================================================

/**
 * @deprecated Rollout config is no longer actively used.
 * Kept for backwards compatibility with admin UI.
 */
export async function updateRolloutConfig(
  config: Partial<DealCardV2RolloutConfig>
): Promise<{ success: boolean; error?: string }> {
  try {
    const current = await fetchRolloutConfig();
    const updated: DealCardV2RolloutConfig = {
      ...current,
      ...config,
    };
    
     
    const { error } = await supabase
      .from("app_config")
      .upsert({
        key: ROLLOUT_CONFIG_KEY,
        value_json: updated as any,
        updated_at: new Date().toISOString(),
      } as any);
    
    if (error) {
      console.error("[V2Rollout] Failed to update config:", error);
      return { success: false, error: error.message };
    }
    
    clearRolloutConfigCache();
    console.log("[V2Rollout] Config updated (deprecated):", updated);
    return { success: true };
  } catch (err) {
    console.error("[V2Rollout] Error updating config:", err);
    return { success: false, error: String(err) };
  }
}
